import { FunctionComponent, RefObject } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { Dropdown, DropdownTrigger, DropdownContent } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { useMergeTags } from '../../hooks/useMergeTags';
import styles from './MergeTagPicker.module.scss';

interface Props {
    trigger: { event?: string; anchor?: string };
    inputRef?: RefObject<HTMLTextAreaElement | HTMLInputElement>;
    value: string;
    onChange: (value: string) => void;
}

/**
 * Inserts a merge tag at the caret. Scoped to the trigger: a "client created"
 * automation carries no appointment, and a required tag it can't fill
 * suppresses the send entirely.
 */
const MergeTagPicker: FunctionComponent<Props> = ({ trigger, inputRef, value, onChange }) => {
    const { tags, loading } = useMergeTags(trigger);

    const insert = (token: string) => {
        const input = inputRef?.current;

        if (!input) {
            onChange(`${value}${value && !value.endsWith(' ') ? ' ' : ''}${token}`);
            return;
        }

        const start = input.selectionStart ?? value.length;
        const end = input.selectionEnd ?? value.length;
        const next = `${value.slice(0, start)}${token}${value.slice(end)}`;

        onChange(next);

        requestAnimationFrame(() => {
            input.focus();
            const caret = start + token.length;
            input.setSelectionRange(caret, caret);
        });
    };

    if (!loading && tags.length === 0) return null;

    return (
        <Dropdown>
            <DropdownTrigger>
                <button
                    type="button"
                    className={styles.trigger}
                    disabled={loading}
                >
                    <FontAwesomeIcon
                        icon={faUser}
                        className={styles.triggerIcon}
                    />
                    Personalization
                </button>
            </DropdownTrigger>
            <DropdownContent
                minWidth={240}
                align="start"
            >
                {tags.map(tag => (
                    <DropdownItem
                        key={tag.token}
                        onSelect={() => insert(tag.token)}
                    >
                        <span className={styles.tagLabel}>{tag.label}</span>
                        <span className={styles.tagToken}>{tag.token}</span>
                    </DropdownItem>
                ))}
            </DropdownContent>
        </Dropdown>
    );
};

export default MergeTagPicker;
